import React from 'react';
import { useField, FieldProps } from 'formik';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import Tooltip from '@material-ui/core/Tooltip';
import { makeStyles } from '@material-ui/core';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import Icons from 'components/Icons';

const useStyles = makeStyles({
  inputField: {
    position: 'relative',

    '& label': {
      maxWidth: '100%'
    },
    display: 'flex',
    flexDirection: 'column'
  },
  icon: {
    width: '24px',
    height: '24px'
  },
  iconBox: {
    position: 'absolute',
    top: 4,
    right: 0,
    width: 'max-content',
    height: 'max-content'
  },
  readOnly: {
    '& div': {
      border: 0,
      color: 'black !important',
      fontWeight: 400,
      '&:before': {
        border: 0
      }
    }
  },
  label: {
    marginBottom: '0.75rem',
    fontSize: '0.75rem',
    fontWeight: 600
  },
  textarea: {
    '& .MuiInputLabel-formControl': {
      zIndex: 1,
      pointerEvents: 'none',
      marginLeft: '4px'
    },
    '& .MuiInput-formControl': {
      background: '#fafafa',
      padding: '6px 4px 7px'
    }
  },
  filledInput: {
    '& .MuiInputBase-input': {
      background: '#dedede',
      padding: '6px',
      borderRadius: '4px 4px 0 0'
    }
  }
});

interface TextProp extends FieldProps {
  label: string;
  placeholder?: string;
  endAdornment?: any;
  startAdornment?: any;
  shrink?: boolean;
  onChange?: any;
  readOnly?: boolean;
  tooltip?: string;
  width: string;
  labelOnTop?: boolean;
  isErrorPresent: boolean;
  multiline?: boolean;
  minRows?: number;
  filled?: boolean;
}

const Text = ({
  field,
  label,
  placeholder,
  endAdornment,
  startAdornment,
  readOnly,
  shrink,
  tooltip,
  width,
  labelOnTop,
  isErrorPresent = false,
  multiline = false,
  minRows = 2,
  filled = false,
  ...props
}: TextProp) => {
  const [, meta] = useField(field.name);
  const classes = useStyles();

  const { error, touched } = meta;
  const isError = isErrorPresent
    ? isErrorPresent
    : typeof error === 'string' && touched;

  return (
    <div className={classes.inputField} style={{ width: width ? width : '' }}>
      {labelOnTop && label && (
        <label
          style={{ color: isError ? 'red' : 'inherit' }}
          className={classes.label}
        >
          {label}
        </label>
      )}
      <TextField
        style={{ width: '100%' }}
        label={!labelOnTop && label}
        className={`
          ${readOnly ? classes.readOnly : ''} 
          ${multiline ? classes.textarea : ''}
          ${filled ? classes.filledInput : ''}
        `}
        placeholder={placeholder}
        multiline={multiline}
        minRows={minRows}
        InputProps={{
          endAdornment: endAdornment && (
            <InputAdornment disableTypography position="end">
              {endAdornment}
            </InputAdornment>
          ),
          startAdornment: startAdornment && (
            <InputAdornment disableTypography position="end">
              {startAdornment}
            </InputAdornment>
          )
        }}
        InputLabelProps={{
          shrink: shrink
        }}
        error={isError}
        {...props}
        {...field}
      />
      <div className={classes.iconBox}>
        {isError && (
          <Tooltip title={error || ''}>
            <ErrorOutlineIcon
              className={classes.icon}
              style={{ color: 'red' }}
            />
          </Tooltip>
        )}
        {tooltip && (
          <Tooltip title={tooltip} arrow>
            <Icons.HelpIcon className={classes.icon} />
          </Tooltip>
        )}
      </div>
    </div>
  );
};

export default Text;
